import React, { useEffect, useState, useContext } from "react";
import { AppContext } from "../../contexts/AppContext";
import PropTypes from "prop-types";
import { Form, Input, Row, Col, Select, Radio } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
  validatePhoneNumber,
  validatePostalCode,
  validateKana,
  validateEmail,
} from "../../utils/validationUtils";
import { GENDER_TYPES } from "../../config/setting";
import "./ReservationInputForm.css";
import { DatePicker } from "antd-mobile";
import dayjs from "dayjs";

const { Option } = Select;
// const fetchAddressFromPostalCode = async (postalCode) => {
//   if (postalCode.length === 7) {
//     return {
//       prefecture: "東京都",
//       city: "新宿区",
//     };
//   }
//   return null;
// };

const defaultFormSetting = {
  cardNo: {
    visible: true,
  },
  name: {
    visible: true,
    required: true,
  },
  kana: {
    visible: true,
    required: true,
  },
  birthDate: {
    visible: true,
    required: true,
  },
  gender: {
    visible: true,
    required: true,
  },
  email: {
    visible: true,
    required: true,
  },
  phoneNumber: {
    visible: true,
    required: true,
  },
  address: {
    visible: false,
    required: false,
  },
};

const ReservationInputForm = ({
  form,
  errors,
  setErrors,
  formSetting = defaultFormSetting,
}) => {
  const [autoCompleteAddress, setAutoCompleteAddress] = useState({
    prefecture: "",
    city: "",
  });
  const [visible, setVisible] = useState(false);

  // デフォルトに引数の内容を上書き
  const finalFormSetting = {
    ...defaultFormSetting,
    ...Object.keys(defaultFormSetting).reduce(
      (acc, key) => ({
        ...acc,
        [key]: { ...defaultFormSetting[key], ...formSetting[key] },
      }),
      {}
    ),
  };
  const { globalLoading } = useContext(AppContext);

  // 初回マウント時にセッションストレージからデータを復元
  useEffect(() => {
    const loadDatas = async () => {
      const savedData = sessionStorage.getItem("inputForm");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        form.setFieldsValue(parsedData);
      }
    };

    loadDatas();
  }, [globalLoading]);

  // ページがリロードまたは閉じる直前にセッションストレージにstateを保存
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && form) {
        sessionStorage.setItem(
          "inputForm",
          JSON.stringify(form.getFieldsValue())
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [form]);

  /**
   * フィールドのフォーカスが外れた時の処理
   * @param {string} fieldName - フィールド名
   */
  const handleBlur = async (fieldName) => {
    try {
      await form.validateFields([fieldName]);
      setErrors({ ...errors, [fieldName]: false });
    } catch (error) {
      setErrors({ ...errors, [fieldName]: true });
    }
  };

  /**
   * 生年月日変更時の処理
   * @param {object} e - イベントオブジェクト
   */
  const handleBirthDateChange = async () => {
    try {
      await form.validateFields(["birthDate"]);
      setErrors({ ...errors, birthDate: false });
    } catch (error) {
      setErrors({ ...errors, birthDate: true });
    }
  };

  /**
   * 郵便番号のフィールドのフォーカスが外れた時の処理
   */
  const handlePostalCodeBlur = async (value) => {
    try {
      await form.validateFields(["postalCode"]);
      setErrors({ ...errors, postalCode: false });

      // const address = await fetchAddressFromPostalCode(value);
      // if (address) {
      //   form.setFieldsValue({
      //     prefecture: address.prefecture,
      //     city: address.city,
      //   });
      //   setAutoCompleteAddress({
      //     prefecture: address.prefecture,
      //     city: address.city,
      //   });
      //   setErrors({
      //     ...errors,
      //     postalCode: false,
      //   });
      // } else {
      //   setErrors({
      //     ...errors,
      //     postalCode: true,
      //   });
      // }
    } catch (error) {
      setErrors({ ...errors, postalCode: true });
    }

    // 強制的にエラー解消
    //setErrors({ ...errors, postalCode: false });
  };
  const currentYear = new Date().getFullYear();

  return (
    <div className="reservation-input-form">
      {finalFormSetting?.cardNo?.visible && (
        <Row gutter={16} className="cardNo-row">
          <span className="explanation-string">
            お持ちの方のみご入力ください
          </span>
          <Col span={24}>
            <Form.Item
              className="ant-label"
              label={<span>診察券番号</span>}
              name="cardNo"
            >
              <Input className="ant-input" />
            </Form.Item>
          </Col>
        </Row>
      )}
      {finalFormSetting?.name?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  hidden={!finalFormSetting?.name?.required}
                  className="emphasis-icon"
                >
                  必須
                </span>
                お名前
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                className="ant-label"
                label={<span>姓</span>}
                name="lastName"
                rules={
                  finalFormSetting?.name?.required
                    ? [{ required: true, message: "姓を入力してください" }]
                    : []
                }
                validateTrigger="onBlur"
              >
                <Input
                  className="ant-input"
                  placeholder="例)山田"
                  suffix={
                    errors.lastName ? <ExclamationCircleOutlined /> : null
                  }
                  onBlur={() => handleBlur("lastName")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                className="ant-label"
                label={<span>名</span>}
                name="firstName"
                rules={
                  finalFormSetting?.name?.required
                    ? [{ required: true, message: "名を入力してください" }]
                    : []
                }
                validateTrigger="onBlur"
              >
                <Input
                  className="ant-input"
                  placeholder="例)太郎"
                  suffix={
                    errors.firstName ? <ExclamationCircleOutlined /> : null
                  }
                  onBlur={() => handleBlur("firstName")}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {finalFormSetting?.name?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  hidden={!finalFormSetting?.kana?.required}
                  className="emphasis-icon"
                >
                  必須
                </span>
                フリガナ
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                className="ant-label"
                label={<span>セイ</span>}
                name="lastKana"
                rules={
                  finalFormSetting?.kana?.required
                    ? [
                        {
                          required: true,
                          message: "カタカナを入力してください",
                        },
                        { validator: validateKana },
                      ]
                    : [{ validator: validateKana }]
                }
                validateTrigger="onBlur"
              >
                <Input
                  className="ant-input"
                  placeholder="ヤマダ"
                  suffix={
                    errors.lastKana ? <ExclamationCircleOutlined /> : null
                  }
                  onBlur={() => handleBlur("lastKana")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                className="ant-label"
                label={<span>メイ</span>}
                name="firstKana"
                rules={
                  finalFormSetting?.kana?.required
                    ? [
                        {
                          required: true,
                          message: "カタカナを入力してください",
                        },
                        { validator: validateKana },
                      ]
                    : [{ validator: validateKana }]
                }
                validateTrigger="onBlur"
              >
                <Input
                  className="ant-input"
                  placeholder="例)タロウ"
                  suffix={
                    errors.firstKana ? <ExclamationCircleOutlined /> : null
                  }
                  onBlur={() => handleBlur("firstKana")}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {finalFormSetting?.birthDate?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  className="emphasis-icon"
                  hidden={!finalFormSetting?.birthDate?.required}
                >
                  必須
                </span>
                生年月日
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <DatePicker
                title="生年月日を選択"
                visible={visible}
                max={new Date()}
                min={new Date(currentYear - 120, 0, 1)}
                confirmText={"確定"}
                cancelText={"取消"}
                defaultValue={new Date(1980, 0, 1)}
                onConfirm={(date) => {
                  form.setFieldsValue({
                    birthDate: dayjs(date).format("YYYY-MM-DD").toString(),
                  });
                }}
                onClose={() => {
                  setVisible(false);
                  handleBirthDateChange();
                }}
                mode="date"
              ></DatePicker>
              <Form.Item
                className="ant-label"
                name="birthDate"
                rules={
                  finalFormSetting?.birthDate?.required
                    ? [
                        {
                          required: true,
                          message: "生年月日を選択してください",
                        },
                      ]
                    : []
                }
              >
                <Input
                  className="ant-input custom-date-input"
                  placeholder="1980-01-01"
                  suffix={
                    errors.birthDate ? <ExclamationCircleOutlined /> : null
                  }
                  onClick={() => {
                    setVisible(true);
                  }}
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {finalFormSetting?.gender?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  className="emphasis-icon"
                  hidden={!finalFormSetting?.gender?.required}
                >
                  必須
                </span>
                性別
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                className="ant-label"
                name="gender"
                rules={
                  finalFormSetting?.gender?.required
                    ? [{ required: true, message: "性別を選択してください" }]
                    : []
                }
                validateTrigger="onBlur"
              >
                <Radio.Group onBlur={() => handleBlur("gender")}>
                  <Radio value={GENDER_TYPES.MALE.id}>
                    {GENDER_TYPES.MALE.name}
                  </Radio>
                  <Radio value={GENDER_TYPES.FEMALE.id}>
                    {GENDER_TYPES.FEMALE.name}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {finalFormSetting?.email?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  className="emphasis-icon"
                  hidden={!finalFormSetting?.email?.required}
                >
                  必須
                </span>
                メールアドレス
              </div>
            </Col>
          </Row>
          <Row gutter={16} className="email-row">
            <span className="explanation-string">
              予約完了、問診依頼のメールを送信させていただきます
            </span>
            <Col span={24}>
              <Form.Item
                className="ant-label"
                name="email"
                rules={
                  finalFormSetting?.email?.required
                    ? [
                        {
                          required: true,
                          message: "メールアドレスを入力してください",
                        },
                        { validator: validateEmail },
                      ]
                    : [{ validator: validateEmail }]
                }
              >
                <Input
                  className="ant-input"
                  placeholder="例)example@example.com"
                  suffix={errors.email ? <ExclamationCircleOutlined /> : null}
                  onBlur={() => handleBlur("email")}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {finalFormSetting?.phoneNumber?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  className="emphasis-icon"
                  hidden={!finalFormSetting?.phoneNumber?.required}
                >
                  必須
                </span>
                電話番号
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                className="ant-label"
                name="phoneNumber"
                rules={
                  finalFormSetting?.phoneNumber?.required
                    ? [
                        {
                          required: true,
                          message: "電話番号を入力してください",
                        },
                        { validator: validatePhoneNumber },
                      ]
                    : [{ validator: validatePhoneNumber }]
                }
                validateTrigger="onBlur"
              >
                <Input
                  className="ant-input"
                  placeholder="例)09012345678"
                  suffix={
                    errors.phoneNumber ? <ExclamationCircleOutlined /> : null
                  }
                  onBlur={() => handleBlur("phoneNumber")}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {finalFormSetting?.address?.visible && (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <div className="form-label">
                <span
                  className="emphasis-icon"
                  hidden={!finalFormSetting?.address?.required}
                >
                  必須
                </span>
                住所
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                className="ant-label"
                label={<span>郵便番号</span>}
                name="postalCode"
                rules={
                  finalFormSetting?.address?.required
                    ? [
                        {
                          required: true,
                          message: "郵便番号を入力してください",
                        },
                        { validator: validatePostalCode },
                      ]
                    : [{ validator: validatePostalCode }]
                }
                validateTrigger="onBlur"
                validateStatus={errors.postalCode ? "error" : ""}
              >
                <Input
                  className="ant-input"
                  placeholder="例)1234567"
                  maxLength={7}
                  onBlur={(e) => handlePostalCodeBlur(e.target.value)}
                  suffix={
                    errors.postalCode ? <ExclamationCircleOutlined /> : null
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                className="ant-label"
                label={<span>都道府県</span>}
                name="prefecture"
                rules={
                  finalFormSetting?.address?.required
                    ? [
                        {
                          required: true,
                          message: "都道府県を選択してください",
                        },
                      ]
                    : []
                }
                validateStatus={errors.prefecture ? "error" : ""}
                help={
                  errors.prefecture ? (
                    <span className="ant-validation-message">
                      都道府県を選択してください
                    </span>
                  ) : (
                    ""
                  )
                }
              >
                <Select
                  className="ant-input"
                  placeholder="選択してください"
                  onBlur={() => handleBlur("prefecture")}
                  value={autoCompleteAddress.prefecture || undefined}
                  suffixIcon={
                    errors.prefecture ? (
                      <ExclamationCircleOutlined style={{ color: "red" }} />
                    ) : null
                  }
                >
                  <Option value="北海道">北海道</Option>
                  <Option value="青森県">青森県</Option>
                  <Option value="岩手県">岩手県</Option>
                  <Option value="宮城県">宮城県</Option>
                  <Option value="秋田県">秋田県</Option>
                  <Option value="山形県">山形県</Option>
                  <Option value="福島県">福島県</Option>
                  <Option value="茨城県">茨城県</Option>
                  <Option value="栃木県">栃木県</Option>
                  <Option value="群馬県">群馬県</Option>
                  <Option value="埼玉県">埼玉県</Option>
                  <Option value="千葉県">千葉県</Option>
                  <Option value="東京都">東京都</Option>
                  <Option value="神奈川県">神奈川県</Option>
                  <Option value="新潟県">新潟県</Option>
                  <Option value="富山県">富山県</Option>
                  <Option value="石川県">石川県</Option>
                  <Option value="福井県">福井県</Option>
                  <Option value="山梨県">山梨県</Option>
                  <Option value="長野県">長野県</Option>
                  <Option value="岐阜県">岐阜県</Option>
                  <Option value="静岡県">静岡県</Option>
                  <Option value="愛知県">愛知県</Option>
                  <Option value="三重県">三重県</Option>
                  <Option value="滋賀県">滋賀県</Option>
                  <Option value="京都府">京都府</Option>
                  <Option value="大阪府">大阪府</Option>
                  <Option value="兵庫県">兵庫県</Option>
                  <Option value="奈良県">奈良県</Option>
                  <Option value="和歌山県">和歌山県</Option>
                  <Option value="鳥取県">鳥取県</Option>
                  <Option value="島根県">島根県</Option>
                  <Option value="岡山県">岡山県</Option>
                  <Option value="広島県">広島県</Option>
                  <Option value="山口県">山口県</Option>
                  <Option value="徳島県">徳島県</Option>
                  <Option value="香川県">香川県</Option>
                  <Option value="愛媛県">愛媛県</Option>
                  <Option value="高知県">高知県</Option>
                  <Option value="福岡県">福岡県</Option>
                  <Option value="佐賀県">佐賀県</Option>
                  <Option value="長崎県">長崎県</Option>
                  <Option value="熊本県">熊本県</Option>
                  <Option value="大分県">大分県</Option>
                  <Option value="宮崎県">宮崎県</Option>
                  <Option value="鹿児島県">鹿児島県</Option>
                  <Option value="沖縄県">沖縄県</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                className="ant-label"
                label={<span>市区群</span>}
                name="city"
                rules={
                  finalFormSetting?.address?.required
                    ? [{ required: true, message: "市区群を入力してください" }]
                    : []
                }
                validateStatus={errors.city ? "error" : ""}
                help={
                  errors.city ? (
                    <span className="ant-validation-message">
                      市区群を入力してください
                    </span>
                  ) : (
                    ""
                  )
                }
              >
                <Input
                  className="ant-input"
                  placeholder="例)新宿区"
                  suffix={errors.city ? <ExclamationCircleOutlined /> : null}
                  onBlur={() => handleBlur("city")}
                  value={autoCompleteAddress.city || undefined}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                className="ant-label"
                label={<span>番地</span>}
                name="address"
                rules={
                  finalFormSetting?.address?.required
                    ? [{ required: true, message: "番地を入力してください" }]
                    : []
                }
                validateStatus={errors.address ? "error" : ""}
                help={
                  errors.address ? (
                    <span className="ant-validation-message">
                      番地を入力してください
                    </span>
                  ) : (
                    ""
                  )
                }
              >
                <Input
                  className="ant-input"
                  placeholder="例)1-1-1"
                  suffix={errors.address ? <ExclamationCircleOutlined /> : null}
                  onBlur={() => handleBlur("address")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                className="ant-label"
                label={<span>{"その他建物名(任意)"}</span>}
                name="building"
              >
                <Input
                  className="ant-input"
                  placeholder="例)サンプルマンション101"
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

// PropTypesを使ってプロパティの型を定義
ReservationInputForm.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  formSetting: PropTypes.object.isRequired,
};

export default ReservationInputForm;
